import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ChatRoomHttpModel, ChatRoomMessageContentHttpModel, ChatRoomMessageHttpModel } from '../models/chat-data-http';
import { ChatRoomCreationStrategy, ChatRoomStatus } from '../models/chat-room.model';
import { ChatConfigService } from './chat-config.service';

interface GetChatRoomsConfig {
  key?: string;
  target?: string;
  strategy?: ChatRoomCreationStrategy;
  status?: ChatRoomStatus;
}

@Injectable({
  providedIn: 'root',
})
export class ChatHttpService {
  constructor(private readonly http: HttpClient, private readonly chatConfigService: ChatConfigService) {}

  public createOrGetChatRoom(
    config: { key: string; target: string; strategy: ChatRoomCreationStrategy },
    metadata?: ChatRoomHttpModel['metadata'],
  ) {
    return this.http.post<{ data: ChatRoomHttpModel }>(this.chatConfigService.chatApiUrl, {
      ...config,
      ...(metadata ? { metadata } : {}),
    });
  }

  public markChatRoomAsResolved(chatRoomId: string) {
    return this.http.put<{ data: ChatRoomHttpModel }>(`${this.chatConfigService.chatApiUrl}/${chatRoomId}`, {
      status: 'resolved',
    });
  }

  public async getAllChatRoomsFor(config: GetChatRoomsConfig) {
    const chatRooms: ChatRoomHttpModel[] = [];
    const PER_PAGE = 100;

    const buildFilterParam = (param: keyof GetChatRoomsConfig) => {
      return config[param] ? { [`filter[${param}]`]: config[param] } : {};
    };

    const getChatRoomsPage = (page: number) => {
      return firstValueFrom(
        this.http.get<{ data: ChatRoomHttpModel[] }>(this.chatConfigService.chatApiUrl, {
          params: {
            page,
            perPage: PER_PAGE,
            ...buildFilterParam('key'),
            ...buildFilterParam('target'),
            ...buildFilterParam('strategy'),
            ...buildFilterParam('status'),
          },
        }),
      );
    };

    let page = 1;
    while (page > 0) {
      const chunk = (await getChatRoomsPage(page)).data;
      chatRooms.push(...chunk);
      if (chunk.length < PER_PAGE) {
        break;
      }
      page += 1;
    }
    return chatRooms;
  }

  public createChatRoomMessage(chatRoomId: string, content: ChatRoomMessageContentHttpModel) {
    return this.http.post<{
      data: ChatRoomMessageHttpModel;
    }>(`${this.chatConfigService.chatApiUrl}/${chatRoomId}/messages`, { content });
  }

  public updateChatRoomMessage(
    chatRoomId: string,
    chatRoomMessageId: string,
    content: ChatRoomMessageContentHttpModel,
  ) {
    return this.http.put<{
      data: ChatRoomMessageHttpModel;
    }>(`${this.chatConfigService.chatApiUrl}/${chatRoomId}/messages/${chatRoomMessageId}`, { content });
  }

  public deleteChatRoomMessage(chatRoomId: string, chatRoomMessageId: string) {
    return this.http.delete(`${this.chatConfigService.chatApiUrl}/${chatRoomId}/messages/${chatRoomMessageId}`);
  }

  public async getAllMessagesFor(chatRoomId: string) {
    const chatRoomMessages: ChatRoomMessageHttpModel[] = [];
    const PER_PAGE = 100;

    const getChatRoomsPage = (page: number) => {
      return firstValueFrom(
        this.http.get<{ data: ChatRoomMessageHttpModel[] }>(
          `${this.chatConfigService.chatApiUrl}/${chatRoomId}/messages`,
          {
            params: {
              page,
              perPage: PER_PAGE,
            },
          },
        ),
      );
    };

    let page = 1;
    while (page > 0) {
      const chunk = (await getChatRoomsPage(page)).data;
      chatRoomMessages.push(...chunk);
      if (chunk.length < PER_PAGE) {
        break;
      }
      page += 1;
    }
    return chatRoomMessages;
  }
}
